import { useRef, useEffect, useState } from 'react';
import { ChevronDown, Clock } from 'lucide-react';
import { Button, StatusPill } from '../ui';
import { canChangeStatus, MANUAL_STATUS_OPTIONS } from '../../utils/caseStatus';
import type { Case } from '../../types';

interface CaseActionBarProps {
  currentCase: Case;
  hasTranslation: boolean;
  isCaseTranslating: boolean;
  isRestarting: boolean;
  isApproved: boolean;
  canApprove: boolean;
  sfStatusLoading: boolean;
  onTranslate: () => void;
  onApprove: () => void;
  onUnapprove: () => void;
  onRestart: () => void;
  onToggleHistory: () => void;
  onGetSfStatus: () => void;
  onStatusChange: (newStatus: string) => void;
}

export function CaseActionBar({
  currentCase,
  hasTranslation,
  isCaseTranslating,
  isRestarting,
  isApproved,
  canApprove,
  sfStatusLoading,
  onTranslate,
  onApprove,
  onUnapprove,
  onRestart,
  onToggleHistory,
  onGetSfStatus,
  onStatusChange,
}: CaseActionBarProps) {
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStatusChange = (newStatus: string) => {
    setStatusDropdownOpen(false);
    onStatusChange(newStatus);
  };

  return (
    <div className="flex items-center justify-between px-4 py-3">
      {/* Left: Status dropdown */}
      <div className="relative" ref={statusDropdownRef} data-onboarding="status-dropdown">
        {canChangeStatus(currentCase.status) ? (
          <button
            onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
            className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-dark-hover hover:bg-gray-100 dark:hover:bg-dark-border border border-gray-200 dark:border-dark-border rounded-lg transition-colors"
          >
            <StatusPill status={currentCase.status} />
            <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${statusDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
        ) : (
          <div className="px-1">
            <StatusPill status={currentCase.status} />
          </div>
        )}
        {statusDropdownOpen && (
          <div className="absolute left-0 mt-2 w-44 bg-white dark:bg-dark-elevated border border-gray-200 dark:border-dark-border rounded-lg shadow-lg z-10">
            <div className="py-1">
              {MANUAL_STATUS_OPTIONS.filter(opt => opt.value !== currentCase.status).map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleStatusChange(option.value)}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:text-purple-700 dark:hover:text-purple-400 transition-colors"
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Center: Secondary actions */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleHistory}
          data-onboarding="history-btn"
        >
          <Clock className="w-4 h-4 mr-1" />
          History
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onGetSfStatus}
          isLoading={sfStatusLoading}
          data-onboarding="sf-status-btn"
        >
          SF Status
        </Button>
        <div className="w-px h-6 bg-gray-200 dark:bg-dark-border" />
        <Button
          variant="secondary"
          size="sm"
          onClick={onTranslate}
          disabled={isCaseTranslating}
          isLoading={isCaseTranslating}
          data-onboarding="translate-btn"
        >
          {hasTranslation ? 'Re-translate' : 'Translate'}
        </Button>
        <Button
          variant="danger"
          size="sm"
          onClick={onRestart}
          disabled={isRestarting}
          isLoading={isRestarting}
          data-onboarding="restart-btn"
        >
          Restart
        </Button>
      </div>

      {/* Right: Primary action */}
      <div className="flex items-center gap-2">
        {isApproved && (
          <Button variant="secondary" size="sm" onClick={onUnapprove}>
            Unapprove
          </Button>
        )}
        {canApprove && (
          <Button variant="primary" size="sm" onClick={onApprove} data-onboarding="approve-btn">
            Approve
          </Button>
        )}
        {!isApproved && !canApprove && (
          <div className="w-24" />
        )}
      </div>
    </div>
  );
}
